import {
  formatDeviceId,
  type AccessHistoryEntry,
  type DeviceInfo,
  type OrganizationSummary,
} from '@viewer/protocol';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api/client';
import { StatusDot } from '../../ui/StatusDot';
import styles from './HomeScreen.module.css';

interface Props {
  organizations: OrganizationSummary[];
  /** Lista local, sem chamar a API. Usada só para visualizar o agrupamento. */
  examples?: Entry[];
  canConnect: boolean;
  onConnect(targetId: string): void;
}

interface Entry {
  org: OrganizationSummary;
  device: DeviceInfo;
}

interface OrgBucket {
  org: OrganizationSummary;
  groups: { key: string; name: string; entries: Entry[] }[];
}

const EXAMPLE_ORG: OrganizationSummary = { id: 'example-org', name: 'Exemplo', role: 'member' };
const HISTORY_ORG: OrganizationSummary = { id: '__history__', name: '', role: 'member' };

function exampleDevice(
  id: string,
  name: string,
  code: string,
  online: boolean,
  group: { id: string; name: string } | null,
): Entry {
  return {
    org: EXAMPLE_ORG,
    device: {
      id,
      code,
      name,
      groupId: group?.id ?? null,
      groupName: group?.name ?? null,
      online,
      lastSeenAt: null,
      unattendedEnabled: false,
      enrolledAt: '2026-01-01T00:00:00.000Z',
      access: { permissions: ['view', 'control'], allowUnattended: false },
    },
  };
}

/** Computadores fictícios para ver a lista agrupada antes de existir uma empresa. */
export const EXAMPLE_DEVICES: Entry[] = [
  exampleDevice('example-recepcao', 'Recepção', '111222333', true, { id: 'example-suporte', name: 'Suporte' }),
  exampleDevice('example-notebook', 'Notebook do suporte', '444555666', true, { id: 'example-suporte', name: 'Suporte' }),
  exampleDevice('example-caixa', 'Caixa 1', '777888999', false, { id: 'example-loja', name: 'Loja' }),
  exampleDevice('example-diretoria', 'Notebook da diretoria', '123123123', true, null),
  exampleDevice('example-estoque', 'Estoque', '456456456', false, null),
];

function historyToEntry(item: AccessHistoryEntry): Entry {
  return {
    org: HISTORY_ORG,
    device: {
      id: `history-${item.code}`,
      code: item.code,
      name: item.name || formatDeviceId(item.code),
      groupId: null,
      groupName: null,
      online: item.online,
      lastSeenAt: item.lastAccessedAt,
      unattendedEnabled: false,
      enrolledAt: item.lastAccessedAt,
      access: { permissions: ['view', 'control'], allowUnattended: false },
    },
  };
}

/** Computadores das empresas do usuário, agrupados pelo grupo da empresa. */
export function MyDevices({ organizations, examples, canConnect, onConnect }: Props) {
  const [entries, setEntries] = useState<Entry[] | null>(examples ?? null);
  const [history, setHistory] = useState<Entry[]>(examples ? [] : []);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [query, setQuery] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);

  useEffect(() => {
    if (examples) {
      setEntries(examples);
      setHistory([]);
      return;
    }
    let cancelled = false;
    async function load() {
      const [lists, historyRows] = await Promise.all([
        Promise.all(
          organizations.map((org) =>
            api
              .get<DeviceInfo[]>(`/api/orgs/${org.id}/devices`)
              .then((devices) =>
                devices.filter((d) => d.access?.permissions.includes('view')).map((device) => ({ org, device })),
              )
              .catch(() => [] as Entry[]),
          ),
        ),
        api
          .get<AccessHistoryEntry[]>('/api/me/access-history')
          .then((rows) => rows.map(historyToEntry))
          .catch(() => [] as Entry[]),
      ]);
      if (!cancelled) {
        setEntries(lists.flat());
        setHistory(historyRows);
      }
    }
    void load();
    const timer = setInterval(() => void load(), 10_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [organizations, examples]);

  if (entries === null) return <p className={styles.help}>Carregando seus dispositivos...</p>;
  if (entries.length === 0 && history.length === 0) {
    return (
      <p className={styles.help}>
        {organizations.length === 0
          ? 'Você ainda não participa de nenhuma empresa.'
          : 'Nenhum computador de empresa liberado para você ainda.'}
      </p>
    );
  }

  const visibleOrg = entries.filter((entry) => matchesDevice(entry, query, onlineOnly));
  const visibleHistory = history.filter((entry) => matchesDevice(entry, query, onlineOnly));
  const orgs = bucketByGroup(visibleOrg);
  const historyOpen = !collapsed.has('history');

  function toggle(key: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className={styles.deviceTree}>
      <div className={styles.deviceTools}>
        <label className={styles.searchField} htmlFor="device-search">
          <span className={styles.srOnly}>Buscar dispositivo</span>
          <input
            id="device-search"
            className={styles.searchInput}
            type="search"
            placeholder="Buscar dispositivo..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <label className={styles.onlineToggle}>
          <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} />
          Somente online
        </label>
      </div>
      {visibleOrg.length === 0 && visibleHistory.length === 0 ? (
        <p className={styles.help}>Nenhum dispositivo encontrado.</p>
      ) : (
        <div className={styles.deviceScroll}>
          {orgs.map((bucket) => (
            <section key={bucket.org.id}>
              {orgs.length > 1 && <p className={styles.orgName}>{bucket.org.name}</p>}
              {bucket.groups.length === 1 && bucket.groups[0]?.key.endsWith(':') ? (
                <DeviceRows entries={bucket.groups[0].entries} flat canConnect={canConnect} onConnect={onConnect} />
              ) : (
                bucket.groups.map((group) => {
                  const open = !collapsed.has(group.key);
                  return (
                    <div key={group.key} className={styles.group}>
                      <button
                        type="button"
                        className={styles.groupToggle}
                        aria-expanded={open}
                        onClick={() => toggle(group.key)}
                      >
                        <span className={open ? styles.chevronOpen : styles.chevron} aria-hidden />
                        <span>
                          {group.name} · {group.entries.length}
                        </span>
                      </button>
                      {open && <DeviceRows entries={group.entries} canConnect={canConnect} onConnect={onConnect} />}
                    </div>
                  );
                })
              )}
            </section>
          ))}
          {visibleHistory.length > 0 && (
            <section>
              <div className={styles.group}>
                <button
                  type="button"
                  className={styles.groupToggle}
                  aria-expanded={historyOpen}
                  onClick={() => toggle('history')}
                >
                  <span className={historyOpen ? styles.chevronOpen : styles.chevron} aria-hidden />
                  <span>
                    Histórico de acessos · {visibleHistory.length}
                  </span>
                </button>
                {historyOpen && (
                  <DeviceRows entries={visibleHistory} canConnect={canConnect} onConnect={onConnect} />
                )}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function matchesDevice(entry: Entry, query: string, onlineOnly: boolean) {
  if (onlineOnly && !entry.device.online) return false;
  const text = query.trim().toLowerCase();
  if (!text) return true;
  const digits = text.replace(/\D/g, '');
  return (
    entry.device.name.toLowerCase().includes(text) ||
    formatDeviceId(entry.device.code).toLowerCase().includes(text) ||
    (digits.length > 0 && entry.device.code.includes(digits))
  );
}

function DeviceRows({
  entries,
  flat = false,
  canConnect,
  onConnect,
}: {
  entries: Entry[];
  flat?: boolean;
  canConnect: boolean;
  onConnect(targetId: string): void;
}) {
  return (
    <ul className={`${styles.deviceLines} ${flat ? styles.deviceLinesFlat : ''}`}>
      {entries.map(({ org, device }) => {
        const example = device.id.startsWith('example-');
        const unavailable = !device.online || (!example && !canConnect);
        return (
          <li key={`${org.id}:${device.id}`} className={device.online ? styles.deviceLine : styles.deviceLineOffline}>
            <div className={styles.deviceIdentity}>
              <span
                className={styles.deviceLineName}
                title={device.access && !device.access.permissions.includes('control') ? 'Somente visualização' : device.name}
              >
                {device.name}
              </span>
              <span className={styles.deviceLineId}>{formatDeviceId(device.code)}</span>
            </div>
            <div className={styles.deviceLineAside}>
              <span className={styles.devicePresence}>
                <StatusDot tone={device.online ? 'success' : 'danger'} />
                {device.online ? 'Online' : 'Offline'}
              </span>
              <button
                type="button"
                className={styles.rowConnect}
                disabled={unavailable}
                aria-disabled={unavailable}
                title={example ? 'Dispositivo de exemplo' : device.online ? undefined : 'Computador offline'}
                onClick={() => {
                  if (example || !device.online || !canConnect) return;
                  onConnect(device.code);
                }}
              >
                Conectar
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function bucketByGroup(entries: Entry[]): OrgBucket[] {
  const orgs = new Map<string, { org: OrganizationSummary; groups: Map<string, Entry[]> }>();
  for (const entry of entries) {
    let bucket = orgs.get(entry.org.id);
    if (!bucket) {
      bucket = { org: entry.org, groups: new Map() };
      orgs.set(entry.org.id, bucket);
    }
    const groupId = entry.device.groupId ?? '';
    const list = bucket.groups.get(groupId) ?? [];
    list.push(entry);
    bucket.groups.set(groupId, list);
  }

  return [...orgs.values()]
    .sort((a, b) => a.org.name.localeCompare(b.org.name, 'pt'))
    .map((bucket) => ({
      org: bucket.org,
      groups: [...bucket.groups.entries()]
        .sort(([a], [b]) => {
          if (a === '') return 1;
          if (b === '') return -1;
          const nameA = bucket.groups.get(a)?.[0]?.device.groupName ?? '';
          const nameB = bucket.groups.get(b)?.[0]?.device.groupName ?? '';
          return nameA.localeCompare(nameB, 'pt');
        })
        .map(([groupId, list]) => ({
          key: `${bucket.org.id}:${groupId}`,
          name: list[0]?.device.groupName ?? 'Sem grupo',
          entries: list.sort(
            (a, b) =>
              Number(b.device.online) - Number(a.device.online) || a.device.name.localeCompare(b.device.name, 'pt'),
          ),
        })),
    }));
}
